import { useState } from 'react'
import { MetadataSchema } from 'src/lib/metadata'
import { MetadataType } from 'types/graphql'
import Button from 'src/components/Button'
import * as Form from 'src/components/Form'
import * as ListBox from 'src/components/ListBox'
import { Controller, useForm } from '@redwoodjs/forms'
import { useMutation } from '@redwoodjs/web'
import { BULK_ADD_TO_METADATA_SCHEMA_MUTATION } from 'src/components/Metadata'
import { reportMutationError } from 'src/lib/reportError'
import kebabCase from 'lodash.kebabcase'

type MetadataWizardProps = {
  missingMetadataSchema: MetadataSchema
  onComplete: () => void
  onCancel: () => void
}

type MetadataFieldConfig = {
  displayName: string
  type: MetadataType
  key: string
}

const MetadataWizard = ({ missingMetadataSchema, onComplete, onCancel }: MetadataWizardProps) => {
  const [bulkAddToMetadataSchema] = useMutation(BULK_ADD_TO_METADATA_SCHEMA_MUTATION)
  const [saving, setSaving] = useState(false)

  // Convert schema to array for easier manipulation in the form
  const metadataFields = Object.entries(missingMetadataSchema).map(([key, config]) => ({
    key,
    displayName: config.displayName,
    type: config.type
  }))

  const formMethods = useForm({
    defaultValues: {
      fields: metadataFields
    }
  })

  const handleSaveAll = async (data: { fields: MetadataFieldConfig[] }) => {
    try {
      setSaving(true)

      const fieldsToSave = data.fields

      // Prepare batch of fields to save
      const batch = fieldsToSave.map(field => ({
        key: kebabCase(field.displayName),
        displayName: field.displayName,
        type: field.type
      }))

      const variables = { input: { batch } }

      const { errors } = await bulkAddToMetadataSchema({
        variables,
        awaitRefetchQueries: true,
      })

      if (errors) {
        reportMutationError({
          errors,
          variables,
          message: `Error bulk updating metadata schema`
        })
        setSaving(false)
        return
      }

      // All fields saved successfully
      onComplete()
    } catch (error) {
      console.error("Error saving metadata fields:", error)
      setSaving(false)
    }
  }

  const metadataTypes = [
    'String',
    'Number',
    'Boolean',
    'URL',
    'Price',
    'Option',
  ]

  const displayValue = (v: MetadataType | undefined) =>
    v ? (v === 'Price' ? 'Currency' : v) : 'Select Type'

  return (
    <div className="p-6 flex flex-col gap-4">
      <div className="flex justify-between items-center">
        <div className="text-lg font-medium">Configure Metadata Fields</div>
        <div className="text-sm text-gray-500">
          {metadataFields.length} fields detected
        </div>
      </div>

      <div className="bg-blue-50 p-4 rounded-md">
        <div className="text-sm font-medium text-blue-800">
          New metadata fields detected
        </div>
        <div className="text-sm text-blue-700 mt-1">
          The imported file contains {metadataFields.length} fields that need to be configured.
          Select a data type for each field below to continue with the import.
        </div>

      </div>

      <Form.Form
        formMethods={formMethods}
        onSubmit={handleSaveAll}
        className="flex flex-col gap-4"
      >
        <div className="border border-gray-200 rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Field Name
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data Type
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {metadataFields.map((field, index) => (
                <tr key={field.key} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-4 py-3 text-sm">
                    {field.displayName}
                    <input type="hidden" {...formMethods.register(`fields.${index}.displayName`)} />
                  </td>
                  <td className="px-4 py-3">
                    <Controller
                      name={`fields.${index}.type`}
                      rules={{ required: true }}
                      render={({ field: { onChange, value, ref } }) => (
                        <ListBox.ListBox onChange={onChange} value={value} ref={ref}>
                          {({ open }) => (
                            <div className="relative">
                              <ListBox.Button displayValue={displayValue(value)} className="text-sm" />
                              <ListBox.Options open={open} align="left">
                                {metadataTypes.map(type => (
                                  <ListBox.Option
                                    key={type}
                                    value={type}
                                    display={displayValue(type)}
                                  />
                                ))}
                              </ListBox.Options>
                            </div>
                          )}
                        </ListBox.ListBox>
                      )}
                    />
                    <input type="hidden" {...formMethods.register(`fields.${index}.key`)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex justify-between mt-4">
          <Button onClick={onCancel} disabled={saving}>
            Cancel Import
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save All and Continue'}
          </Button>
        </div>
      </Form.Form>
    </div>
  )
}

export default MetadataWizard
